import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/lib/toast";
import {
  Sparkles, Inbox, Check, X, Trash2, FileText, Wand2, Mail, RefreshCw,
  Plus, Building2, ClipboardCheck, XCircle, Play, Mailbox, Pencil, FileImage,
  ExternalLink, Minus, Search, Archive, ArchiveRestore,
} from "lucide-react";
import { useConfirm } from "@/components/ConfirmModal";
import Modal from "@/components/Modal";
import { useAppSettings } from "@/hooks/useAppSettings";

// ─── Status display config ───────────────────────────────────────────────────
const STATUS_LABEL_FALLBACK = {
  new: "Awaiting Review",
  parsing: "Parsing…",
  parsed: "Awaiting Review",
  approved: "Approved",
  rejected: "Rejected",
  parse_error: "Parse failed",
};
const STATUS_TONE = {
  new: "warning",
  parsing: "info",
  parsed: "warning",
  approved: "success",
  rejected: "error",
  parse_error: "error",
};

const TABS = [
  { key: "review",   label: "Review",    match: (p) => p.Status === "parsed" || p.Status === "new" || p.Status === "parsing" || p.Status === "parse_error" },
  { key: "approved", label: "Approved",  match: (p) => p.Status === "approved" },
  { key: "rejected", label: "Rejected",  match: (p) => p.Status === "rejected" },
  { key: "archive",  label: "Archive",   match: () => true },  // archive is virtual — filtered by p.Archived
  { key: "all",      label: "All",       match: () => true },
];

const DATE_RANGES = [
  { key: "all",   label: "All time" },
  { key: "today", label: "Today" },
  { key: "7d",    label: "7 days" },
  { key: "30d",   label: "30 days" },
];

function withinRange(po, rangeKey) {
  if (rangeKey === "all") return true;
  const created = new Date(po.created_date || po.updated_date || 0);
  if (Number.isNaN(created.getTime())) return true;
  const now = new Date();
  if (rangeKey === "today") {
    return created.toDateString() === now.toDateString();
  }
  const days = rangeKey === "7d" ? 7 : rangeKey === "30d" ? 30 : 0;
  const cutoff = new Date(now.getTime() - days * 86400000);
  return created >= cutoff;
}

function matchesQuery(po, q) {
  if (!q) return true;
  const needle = q.toLowerCase();
  const hay = [
    po.Parsed_Data?.customer_name,
    po.Parsed_Data?.po_reference,
    po.Order_Reference,
    po.Source_Email,
    po.Raw_Text,
  ].filter(Boolean).join(" ").toLowerCase();
  return hay.includes(needle);
}

// Poll a PO until Gemini parsing finishes. Returns the latest PO doc.
// Used by the upload flow which returns immediately with Status='parsing'.
// `onTick(elapsedMs)` is called every interval so the UI can show progress.
async function pollPoUntilParsed(poId, { intervalMs = 2500, timeoutMs = 180_000, onTick } = {}) {
  const start = Date.now();
  const deadline = start + timeoutMs;
  while (Date.now() < deadline) {
    try {
      const { data } = await api.get(`/purchase-orders/${poId}`);
      if (data?.Status !== "parsing") return data;
    } catch {
      // transient — keep polling until timeout
    }
    onTick?.(Date.now() - start);
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  // Timed out — return whatever we have so user can refresh manually.
  const { data } = await api.get(`/purchase-orders/${poId}`);
  return data;
}

export default function PurchaseOrdersPage() {
  const toast = useToast();
  const confirm = useConfirm();
  const settings = useAppSettings();
  const statusLabelOf = (key) =>
    settings?.PO_Status_Labels?.[key] || STATUS_LABEL_FALLBACK[key] || key;
  const [pos, setPos] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("review");
  const [selectedId, setSelectedId] = useState(null);
  const [showParse, setShowParse] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [bulkIds, setBulkIds] = useState(() => new Set());
  const [searchQ, setSearchQ] = useState("");
  const [dateRange, setDateRange] = useState("all");

  const toggleBulk = (id) => {
    setBulkIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleBulkAll = (visibleIds) => {
    setBulkIds((prev) => {
      const allChecked = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allChecked) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const bulkDelete = async (ids) => {
    const ok = await confirm({
      title: `Delete ${ids.length} PO${ids.length === 1 ? "" : "s"}?`,
      message: "These purchase orders will be permanently removed.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    let failed = 0;
    await Promise.all(ids.map((id) => api.delete(`/purchase-orders/${id}`).catch(() => { failed += 1; })));
    setPos((ps) => ps.filter((p) => !ids.includes(p.id)));
    if (selectedId && ids.includes(selectedId)) setSelectedId(null);
    setBulkIds(new Set());
    if (failed === 0) toast.success(`Deleted ${ids.length} PO${ids.length === 1 ? "" : "s"}.`);
    else toast.error(`Deleted ${ids.length - failed}, ${failed} failed.`);
  };

  const archivePo = async (id, archived) => {
    try {
      const url = archived ? `/purchase-orders/${id}/unarchive` : `/purchase-orders/${id}/archive`;
      const { data } = await api.post(url);
      setPos((ps) => ps.map((p) => (p.id === id ? data : p)));
      if (selectedId === id) setSelectedId(null);
      if (archived) {
        toast.success("PO restored to inbox.");
      } else {
        // Friendly toast with an Undo affordance so the user always has an out.
        toast.success("PO archived. Switch to the Archive tab to view it.", {
          action: { label: "Undo", onClick: () => archivePo(id, true) },
        });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/purchase-orders"); setPos(data); setLoaded(true); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const t = TABS.find((x) => x.key === tab) || TABS[0];
    return pos.filter((p) => {
      if (tab === "archive") {
        if (!p.Archived) return false;
      } else {
        if (p.Archived) return false;
        if (!t.match(p)) return false;
      }
      if (!withinRange(p, dateRange)) return false;
      if (!matchesQuery(p, searchQ)) return false;
      return true;
    });
  }, [pos, tab, searchQ, dateRange]);

  // Counts per tab respect search + date filters but ignore current tab/archive
  // so the user can tell "do I have anything to review even if I'm filtered?".
  const tabCount = (t) => {
    if (t.key === "archive") {
      return pos.filter((p) => p.Archived && withinRange(p, dateRange) && matchesQuery(p, searchQ)).length;
    }
    return pos.filter((p) =>
      t.match(p) &&
      !p.Archived &&
      withinRange(p, dateRange) &&
      matchesQuery(p, searchQ)
    ).length;
  };
  const archivedCount = pos.filter((p) => p.Archived).length;  // surfaced via the Archive tab badge

  const selected = pos.find((p) => p.id === selectedId);
  const [companies, setCompanies] = useState([]);
  useEffect(() => { api.get("/companies").then(({ data }) => setCompanies(data)).catch(() => {}); }, []);
  const linkedCompany = (po) => companies.find((c) => c.id === po.Company_ID);

  const onDelete = async (id) => {
    const ok = await confirm({
      title: "Delete this PO?",
      message: "The purchase order will be permanently removed from the inbox.",
      confirmLabel: "Delete PO",
      danger: true,
    });
    if (!ok) return;
    try { await api.delete(`/purchase-orders/${id}`); setPos((ps) => ps.filter((p) => p.id !== id)); if (selectedId === id) setSelectedId(null); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const updatePO = (fresh) => {
    setPos((ps) => ps.map((p) => (p.id === fresh.id ? fresh : p)));
  };

  return (
    <div className="space-y-4 lg:h-[calc(100vh-7rem)] flex flex-col">
      {err && <div className="es-card p-4 text-sm text-red-700">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr_360px] gap-4 flex-1 lg:min-h-0">
        {/* LEFT PANE - PO LIST */}
        <aside className="es-card flex flex-col min-h-0">
          <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <Inbox className="w-4 h-4 text-brand-600 flex-shrink-0" />
              <h2 className="font-display font-semibold text-sm truncate">Purchase Orders Inbox</h2>
              <span className="es-badge es-badge-neutral flex-shrink-0" data-testid="po-total-count">
                {loaded ? pos.length : "—"}
              </span>
            </div>
            <div className="flex items-center gap-1">
              {settings?.PO_Email_Ingestion_Enabled && (
                <button onClick={load} disabled={loading} title="Refresh" className="p-1.5 hover:bg-zinc-100 rounded-md text-zinc-600" data-testid="po-refresh-btn">
                  <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                </button>
              )}
              <button data-testid="po-new-button" onClick={() => setShowParse(true)} title="Upload a file or paste text to create a new PO" className="w-7 h-7 flex items-center justify-center rounded-md bg-brand-600 hover:bg-brand-700 text-white">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="px-3 pt-2 pb-2 flex gap-1 overflow-x-auto border-b border-zinc-100">
            {TABS.map((t) => {
              const count = tabCount(t);
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  data-testid={`po-tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={[
                    "px-2.5 py-1 text-xs font-semibold rounded-full transition-colors whitespace-nowrap flex items-center gap-1.5",
                    active ? "bg-brand-600 text-white" : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50",
                  ].join(" ")}
                >
                  {t.label}
                  {count > 0 && (
                    <span className={["text-[10px] px-1.5 rounded-full leading-tight",
                      active ? "bg-white/25 text-white" : "bg-zinc-200 text-zinc-700"].join(" ")}>{count}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Search + date range + archive toggle */}
          <div className="px-3 py-2 border-b border-zinc-100 space-y-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-zinc-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                data-testid="po-search-input"
                type="text"
                placeholder={settings?.PO_Email_Ingestion_Enabled ? "Search customer, PO ref, sender…" : "Search customer, PO ref…"}
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                className="es-input pl-7 pr-7 h-8 text-xs w-full"
              />
              {searchQ && (
                <button
                  data-testid="po-search-clear"
                  onClick={() => setSearchQ("")}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-zinc-400 hover:text-zinc-700"
                  aria-label="Clear search"
                ><X className="w-3 h-3" /></button>
              )}
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              <select
                data-testid="po-date-range"
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="text-[11px] font-medium bg-zinc-50 border border-zinc-200 rounded-md px-1.5 py-0.5 h-7 cursor-pointer hover:border-zinc-400"
              >
                {DATE_RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </div>
          </div>
          <div className="px-4 py-2 border-b border-zinc-100 flex items-center gap-2">
            <input
              type="checkbox"
              data-testid="po-select-all"
              checked={filtered.length > 0 && filtered.every((p) => bulkIds.has(p.id))}
              ref={(el) => { if (el) el.indeterminate = filtered.some((p) => bulkIds.has(p.id)) && !filtered.every((p) => bulkIds.has(p.id)); }}
              onChange={() => toggleBulkAll(filtered.map((p) => p.id))}
              className="w-3.5 h-3.5 rounded border-zinc-300 accent-brand-600 cursor-pointer"
            />
            {bulkIds.size > 0 ? (
              <>
                <span className="text-[11px] text-zinc-700 font-medium">{bulkIds.size} selected</span>
                <button
                  data-testid="po-bulk-delete"
                  onClick={() => bulkDelete(Array.from(bulkIds))}
                  className="ml-auto text-[11px] font-semibold text-red-700 hover:text-red-900 hover:bg-red-50 px-2 py-1 rounded-md flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" /> Delete selected
                </button>
              </>
            ) : (
              <span className="text-[11px] text-zinc-500">Select all</span>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-4 py-16 text-center">
                <Mailbox className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
                <p className="text-xs text-zinc-500">No POs in <span className="font-semibold">{tab}</span>. Click + to add one.</p>
              </div>
            ) : filtered.map((p) => {
              const active = selectedId === p.id;
              const company = linkedCompany(p);
              const tone = STATUS_TONE[p.Status] || "neutral";
              return (
                <div
                  key={p.id}
                  data-testid={`po-row-${p.id}`}
                  className={[
                    "w-full text-left px-4 py-3 border-b border-zinc-50 hover:bg-zinc-50 transition-colors flex gap-3 items-start group relative cursor-pointer",
                    active ? "bg-brand-50 hover:bg-brand-50" : "",
                  ].join(" ")}
                  onClick={() => setSelectedId(p.id)}
                >
                  <input
                    type="checkbox"
                    data-testid={`po-bulk-check-${p.id}`}
                    checked={bulkIds.has(p.id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleBulk(p.id)}
                    className="mt-1.5 w-3.5 h-3.5 rounded border-zinc-300 accent-brand-600 cursor-pointer flex-shrink-0"
                  />
                  <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                    tone === "warning" ? "bg-amber-500" :
                    tone === "success" ? "bg-green-600" :
                    tone === "error" ? "bg-red-500" : "bg-zinc-300"
                  }`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-zinc-900 truncate">Purchase order</div>
                    <div className="text-xs text-zinc-600 truncate">{p.Parsed_Data?.customer_name || "Unknown customer"}</div>
                    <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                      <span className={`es-badge es-badge-${tone}`}>{statusLabelOf(p.Status)}</span>
                      {p.Archived && (
                        <span className="text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded bg-zinc-200 text-zinc-700 flex items-center gap-1">
                          <Archive className="w-2.5 h-2.5" /> archived
                        </span>
                      )}
                      {company && (
                        <span className="text-[11px] text-zinc-500 truncate inline-flex items-center gap-1">
                          <span className="text-zinc-400">·</span>{company.Company_Name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="absolute right-1 top-1 flex items-center gap-0.5 opacity-60 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                    {(p.Status === "approved" || p.Status === "rejected") && (
                      <button
                        data-testid={`po-row-archive-${p.id}`}
                        onClick={(e) => { e.stopPropagation(); archivePo(p.id, p.Archived); }}
                        className="p-1.5 rounded-md text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100"
                        title={p.Archived ? "Restore to inbox" : "Archive PO"}
                        aria-label={p.Archived ? "Restore PO" : "Archive PO"}
                      >
                        {p.Archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                      </button>
                    )}
                    <button
                      data-testid={`po-row-delete-${p.id}`}
                      onClick={(e) => { e.stopPropagation(); onDelete(p.id); }}
                      className="p-1.5 rounded-md text-zinc-400 hover:text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-200"
                      title="Delete PO"
                      aria-label="Delete PO"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </aside>

        {/* MIDDLE PANE - WORKFLOW */}
        <section className="overflow-y-auto pr-1">
          {selected ? (
            <POWorkflow
              key={selected.id}
              po={selected}
              companies={companies}
              setCompanies={setCompanies}
              onUpdated={(fresh) => updatePO(fresh)}
              onDelete={() => onDelete(selected.id)}
              onReload={load}
              onEditDetails={() => setShowEdit(true)}
            />
          ) : (
            <EmptyMiddle />
          )}
        </section>

        {/* RIGHT PANE - DOCUMENT PREVIEW */}
        <aside className="es-card flex flex-col min-h-0">
          <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-brand-600" />
              <h2 className="font-display font-semibold text-sm">Document Preview</h2>
            </div>
            {selected && (
              <button
                data-testid="po-edit-details"
                onClick={() => setShowEdit(true)}
                className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-md border border-zinc-200 hover:border-brand-500 hover:text-brand-700 text-zinc-600"
              >
                <Pencil className="w-3 h-3" /> Edit Details
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {selected ? <RightPanePreview po={selected} /> : (
              <div className="text-center text-xs text-zinc-400 mt-12 px-6">Select a PO to preview the original document and extracted details.</div>
            )}
          </div>
        </aside>
      </div>

      {showParse && <ParseModal onClose={() => setShowParse(false)} onParsed={(p) => { setPos((prev) => [p, ...prev]); setShowParse(false); setSelectedId(p.id); }} />}
      {showEdit && selected && (
        <EditDetailsModal
          po={selected}
          companies={companies}
          onClose={() => setShowEdit(false)}
          onSaved={(fresh) => { updatePO(fresh); setShowEdit(false); }}
        />
      )}
    </div>
  );
}

// ─── Empty middle pane ───────────────────────────────────────────────────────
function EmptyMiddle() {
  return (
    <div className="es-card h-full flex flex-col items-center justify-center p-12 text-center">
      <div className="w-14 h-14 rounded-2xl bg-brand-50 flex items-center justify-center mb-4">
        <ClipboardCheck className="w-7 h-7 text-brand-600" />
      </div>
      <h3 className="font-display font-semibold text-zinc-900 mb-1">PO Inbound Processing</h3>
      <p className="text-sm text-zinc-500 max-w-sm">Select a PO from the inbox to review the email, run the parser, and approve into the order pipeline.</p>
    </div>
  );
}

// ─── Middle pane: stacked stage workflow ─────────────────────────────────────
function POWorkflow({ po, companies, setCompanies, onUpdated, onDelete, onReload, onEditDetails }) {
  const toast = useToast();
  const settings = useAppSettings();
  const emailIngestionOn = Boolean(settings?.PO_Email_Ingestion_Enabled);
  const [products, setProducts] = useState([]);
  const [companyId, setCompanyId] = useState(po.Company_ID || "");
  const [deliveryAddr, setDeliveryAddr] = useState(po.Parsed_Data?.delivery_address || "");
  const [orderRef, setOrderRef] = useState(po.Parsed_Data?.po_reference || po.Order_Reference || "");
  const [comments, setComments] = useState(po.Comments || po.Parsed_Data?.comments || po.Parsed_Data?.notes || "");
  const [lines, setLines] = useState(
    (po.Parsed_Data?.lines || []).map((l) => ({
      Product_ID: "",
      product_name: l.product_name,
      Quantity_Ordered: l.quantity || 0,
      Notes: l.notes || "",
    }))
  );
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reparsing, setReparsing] = useState(false);
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [err, setErr] = useState("");

  const isApproved = po.Status === "approved";
  const isRejected = po.Status === "rejected";
  const isLocked = isApproved || isRejected;
  const linkedOrders = (po.Linked_Order_IDs || []).length;
  const totalQty = lines.reduce((s, l) => s + (Number(l.Quantity_Ordered) || 0), 0);

  useEffect(() => {
    api.get("/inventory").then(({ data }) => {
      setProducts(data);
      // Auto-match line items
      setLines((prev) => prev.map((ln) => {
        const target = ln.product_name?.toLowerCase() || "";
        const m = data.find((pr) => target.includes(pr.Product_Name.toLowerCase()) || pr.Product_Name.toLowerCase().includes(target));
        return m ? { ...ln, Product_ID: m.id } : ln;
      }));
    });
    // Auto-match company
    if (!companyId) {
      const cn = po.Parsed_Data?.customer_name?.toLowerCase();
      if (cn) {
        const m = companies.find((co) => co.Company_Name?.toLowerCase().includes(cn) || cn.includes(co.Company_Name?.toLowerCase() || ""));
        if (m) setCompanyId(m.id);
      }
    }
  }, [po.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const parsedName = po.Parsed_Data?.customer_name;
  const noMatch = parsedName && !companies.some((c) => {
    const cn = parsedName.toLowerCase();
    return c.Company_Name?.toLowerCase().includes(cn) || cn.includes(c.Company_Name?.toLowerCase() || "");
  });
  const resolvedCompany = companies.find((c) => c.id === companyId);

  const createCompanyFromPO = async () => {
    setCreatingCompany(true);
    try {
      const { data } = await api.post(`/purchase-orders/${po.id}/create-company`);
      const created = data.company;
      setCompanies((prev) => [created, ...prev.filter((c) => c.id !== created.id)]);
      setCompanyId(created.id);
      toast.success(data.message);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setCreatingCompany(false); }
  };

  const runParser = async () => {
    setReparsing(true); setErr("");
    try {
      const { data } = await api.post(`/purchase-orders/${po.id}/reparse`);
      onUpdated(data);
      toast.success("Parser re-run complete.");
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setReparsing(false); }
  };

  const approve = async () => {
    setApproving(true); setErr("");
    const payload = {
      Company_ID: companyId,
      Lines: lines.filter((l) => l.Product_ID && l.Quantity_Ordered > 0),
      Delivery_Address: deliveryAddr,
    };
    if (!payload.Company_ID || payload.Lines.length === 0) {
      setErr("Pick a company and at least one valid line (with a matched product)."); setApproving(false); return;
    }
    try {
      const { data } = await api.post(`/purchase-orders/${po.id}/approve`, payload);
      toast.success(`Created ${data.orders_created} draft order(s) in the pipeline.`);
      onReload();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setApproving(false); }
  };

  const reject = async () => {
    if (!window.confirm("Reject this PO? It will be moved to the Rejected tab.")) return;
    setRejecting(true);
    try {
      await api.post(`/purchase-orders/${po.id}/reject`);
      toast.success("PO rejected.");
      onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setRejecting(false); }
  };

  const statusTone = STATUS_TONE[po.Status] || "neutral";
  const statusLabel = STATUS_LABEL_FALLBACK[po.Status] || po.Status;

  return (
    <div className="space-y-4 pb-4">
      {/* HEADER CARD */}
      <div className="es-card p-5 flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display font-semibold text-lg text-zinc-900">Purchase order</h2>
            <span className={`es-badge es-badge-${statusTone}`} data-testid="po-status-badge">{statusLabel}</span>
          </div>
          <div className="text-sm text-zinc-500 mt-1 truncate">
            <span className="font-medium text-zinc-700">{po.Parsed_Data?.customer_name || "—"}</span>
            {emailIngestionOn && po.Source_Email && <span> · {po.Source_Email}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!isLocked && (
            <button
              data-testid="po-run-parser"
              onClick={runParser}
              disabled={reparsing}
              className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold disabled:opacity-50"
            >
              <Play className="w-4 h-4 fill-white" />
              {reparsing ? "Running…" : "Run Parser"}
            </button>
          )}
          <button onClick={onDelete} className="text-zinc-400 hover:text-red-600 p-1.5" title="Delete"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>

      {/* STAGE 1 — ORIGINAL TEXT (or EMAIL when ingestion is on) */}
      <StageCard
        index={1}
        icon={emailIngestionOn ? <Mail className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
        title={emailIngestionOn ? "ORIGINAL EMAIL" : "ORIGINAL TEXT"}
      >
        <div className="bg-zinc-50 border border-zinc-100 rounded-lg p-4 max-h-64 overflow-y-auto">
          <pre className="text-[13px] font-mono whitespace-pre-wrap text-zinc-700 leading-relaxed" data-testid="po-original-email">
            {po.Raw_Text?.trim() || "(no raw text)"}
          </pre>
        </div>
      </StageCard>

      {/* STAGE 2 — PO INBOUND PROCESSING */}
      <StageCard
        index={2}
        icon={<ClipboardCheck className="w-4 h-4" />}
        title="PO INBOUND PROCESSING"
        right={
          po.Parsed_Data && Object.keys(po.Parsed_Data).length > 0 ? (
            <span className="es-badge es-badge-success">Auto-Parsed</span>
          ) : null
        }
      >
        {po.Parsed_Data && Object.keys(po.Parsed_Data).length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3.5 flex items-start gap-3 mb-4">
            <div className="w-7 h-7 rounded-full bg-green-600 flex-shrink-0 flex items-center justify-center mt-0.5">
              <Check className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 text-sm text-green-900">
              <div className="font-semibold">Successfully parsed</div>
              <div className="text-green-800/80 text-xs mt-0.5">
                {lines.length} line item{lines.length !== 1 ? "s" : ""} detected
                {resolvedCompany ? ` · Company resolved via CRM match` : noMatch ? " · No CRM match" : ""}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="es-label">Resolved Company</label>
            <div className="flex gap-2">
              <select
                disabled={isLocked}
                data-testid="po-company-select"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                className="es-select w-full flex-1"
              >
                <option value="">Select…</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.Company_Name}</option>)}
              </select>
              {noMatch && !isLocked && !companyId && (
                <button data-testid="po-create-company" onClick={createCompanyFromPO} disabled={creatingCompany} className="es-btn-secondary flex-shrink-0 whitespace-nowrap">
                  <Plus className="w-4 h-4" /> {creatingCompany ? "…" : "Create"}
                </button>
              )}
            </div>
            {noMatch && !isLocked && !companyId && (
              <p className="text-xs text-amber-700 mt-1.5 flex items-center gap-1.5"><Building2 className="w-3 h-3" /> No CRM match — click Create.</p>
            )}
          </div>

          <div>
            <label className="es-label">Order Reference</label>
            <input
              data-testid="po-order-ref"
              disabled={isLocked}
              value={orderRef}
              onChange={(e) => setOrderRef(e.target.value)}
              placeholder="Customer PO number"
              className="es-input"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="es-label">Delivery Address</label>
            <input
              data-testid="po-delivery-addr"
              disabled={isLocked}
              value={deliveryAddr}
              onChange={(e) => setDeliveryAddr(e.target.value)}
              className="es-input"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="es-label">Comments / Instructions</label>
            <textarea
              data-testid="po-comments"
              disabled={isLocked}
              rows={2}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="e.g. pickup from supplier, in season landscape…"
              className="es-input h-auto py-2 resize-none"
            />
          </div>

          <div>
            <label className="es-label">Cards Created in Workflow</label>
            <div className="es-input flex items-center font-mono text-zinc-900 bg-zinc-50">
              {linkedOrders}
            </div>
          </div>

          <div>
            <label className="es-label">Total Qty</label>
            <div className="es-input flex items-center font-mono text-zinc-900 bg-zinc-50">
              {totalQty}
            </div>
          </div>
        </div>

        {/* Lines table */}
        <div className="mt-5">
          <div className="es-overline mb-2">Lines</div>
          <div className="border border-zinc-100 rounded-lg overflow-hidden">
            <table className="es-table">
              <thead>
                <tr>
                  <th>Parsed name</th>
                  <th>Match Product</th>
                  <th className="text-right">Qty</th>
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr><td colSpan={3} className="text-center text-xs text-zinc-400 py-6">No lines detected. Click Run Parser to retry.</td></tr>
                ) : lines.map((ln, idx) => (
                  <tr key={idx} data-testid={`po-line-${idx}`}>
                    <td className="text-sm">{ln.product_name}</td>
                    <td>
                      <select disabled={isLocked} value={ln.Product_ID} onChange={(e) => setLines((prev) => prev.map((l, i) => i === idx ? { ...l, Product_ID: e.target.value } : l))} className="es-select w-full">
                        <option value="">— pick product —</option>
                        {products.map((p) => <option key={p.id} value={p.id}>{p.Product_Name}</option>)}
                      </select>
                    </td>
                    <td className="text-right">
                      <input disabled={isLocked} type="number" value={ln.Quantity_Ordered} onChange={(e) => setLines((prev) => prev.map((l, i) => i === idx ? { ...l, Quantity_Ordered: Number(e.target.value) } : l))} className="es-input w-24 ml-auto text-right font-mono" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {err && <div className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-lg mt-4">{err}</div>}

        {isApproved && (
          <div className="text-sm text-green-700 bg-green-50 border border-green-200 px-3 py-2 rounded-lg mt-4">
            Approved — {linkedOrders} draft order(s) created in the pipeline.
          </div>
        )}
        {isRejected && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg mt-4">
            Rejected — this PO will not be processed.
          </div>
        )}

        {!isLocked && (
          <div className="mt-5 pt-4 border-t border-zinc-100 flex justify-between gap-3">
            <button onClick={reject} disabled={rejecting} data-testid="po-reject" className="es-btn-secondary text-red-700 hover:text-red-800 hover:border-red-300">
              <XCircle className="w-4 h-4" /> {rejecting ? "Rejecting…" : "Reject"}
            </button>
            <button onClick={approve} disabled={approving} data-testid="po-approve" className="es-btn-primary">
              <Check className="w-4 h-4" /> {approving ? "Creating orders…" : "Approve → Create orders"}
            </button>
          </div>
        )}
      </StageCard>
    </div>
  );
}

// ─── Stage card wrapper ──────────────────────────────────────────────────────
function StageCard({ index, icon, title, right, children }) {
  return (
    <div className="es-card overflow-hidden">
      <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/50">
        <div className="flex items-center gap-2.5 text-zinc-700">
          <span className="w-6 h-6 rounded-md bg-white border border-zinc-200 flex items-center justify-center text-brand-600">
            {icon}
          </span>
          <span className="text-[11px] tracking-[0.08em] font-semibold uppercase text-zinc-500">Stage {index} —</span>
          <span className="text-sm font-semibold text-zinc-900">{title}</span>
        </div>
        {right}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

// ─── Right pane: actual document preview with zoom controls ───────────────
function RightPanePreview({ po }) {
  const [zoom, setZoom] = useState(1); // 1 = fit-width; 1.5x = 150%; etc.

  // Resolve absolute URL for the uploaded file (if any)
  const fileUrl = po.Source_File_URL
    ? (po.Source_File_URL.startsWith("http") ? po.Source_File_URL : `${process.env.REACT_APP_BACKEND_URL}${po.Source_File_URL}`)
    : null;
  const mime = po.Source_Mime || "";
  const isPdf = mime === "application/pdf" || (po.Source_Filename || "").toLowerCase().endsWith(".pdf");
  const isImage = mime.startsWith("image/") || /\.(png|jpe?g|webp|gif)$/i.test(po.Source_Filename || "");
  const hasFile = Boolean(fileUrl) && (isPdf || isImage);

  const clamp = (v) => Math.max(0.5, Math.min(4, v));
  const reset = () => setZoom(1);

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="px-3 py-2 border-b border-zinc-100 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 min-w-0 flex-1">
          <FileImage className="w-4 h-4 text-zinc-500 flex-shrink-0" />
          <span className="text-xs font-mono text-zinc-700 truncate" title={po.Source_Filename || ""}>
            {po.Source_Filename || `${po.Source || "paste"}_${po.id?.slice(0, 8)}`}
          </span>
        </div>
        {hasFile && (
          <div className="flex items-center gap-1 ml-auto">
            <button
              data-testid="po-zoom-out"
              onClick={() => setZoom((z) => clamp(z - 0.25))}
              className="w-7 h-7 rounded-md border border-zinc-200 hover:border-brand-500 hover:text-brand-700 inline-flex items-center justify-center"
              title="Zoom out"
              aria-label="Zoom out"
            ><Minus className="w-3.5 h-3.5" /></button>
            <button
              data-testid="po-zoom-reset"
              onClick={reset}
              className="px-2 h-7 text-[11px] font-mono rounded-md border border-zinc-200 hover:border-brand-500 hover:text-brand-700"
              title="Fit width (1x)"
            >{Math.round(zoom * 100)}%</button>
            <button
              data-testid="po-zoom-in"
              onClick={() => setZoom((z) => clamp(z + 0.25))}
              className="w-7 h-7 rounded-md border border-zinc-200 hover:border-brand-500 hover:text-brand-700 inline-flex items-center justify-center"
              title="Zoom in"
              aria-label="Zoom in"
            ><Plus className="w-3.5 h-3.5" /></button>
          </div>
        )}
        {fileUrl && (
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="po-open-new-tab"
            className="text-[11px] inline-flex items-center gap-1 px-2 h-7 rounded-md border border-zinc-200 hover:border-brand-500 hover:text-brand-700 text-zinc-600"
            title="Open in new tab"
          ><ExternalLink className="w-3 h-3" /> Open</a>
        )}
      </div>

      {/* Document body */}
      <div className="flex-1 min-h-0 overflow-auto bg-zinc-100 p-3">
        {!fileUrl && (
          // Pasted text fallback — no attachment to preview
          <div className="bg-zinc-900 rounded-lg p-3 max-h-full overflow-y-auto">
            <pre className="text-[11px] font-mono whitespace-pre-wrap text-zinc-100 leading-relaxed" data-testid="po-document-preview">
              {po.Raw_Text?.trim() || "(no document attached)"}
            </pre>
          </div>
        )}

        {fileUrl && isImage && (
          <div className="inline-block min-w-full">
            <img
              src={fileUrl}
              alt={po.Source_Filename || "PO document"}
              data-testid="po-document-image"
              style={{ width: `${zoom * 100}%`, height: "auto", maxWidth: "none" }}
              className="rounded-lg shadow-sm bg-white block"
            />
          </div>
        )}

        {fileUrl && isPdf && (
          // PDF rendered via the browser's native viewer; iframe size scales
          // with zoom by setting width and a tall fixed height that the inner
          // viewer can scroll. We use a wrapper that grows so the parent scrollbar
          // can pan around the zoomed PDF.
          <div className="inline-block min-w-full">
            <iframe
              src={fileUrl}
              title="PO document"
              data-testid="po-document-iframe"
              style={{ width: `${zoom * 100}%`, height: `${Math.max(600, 600 * zoom)}px` }}
              className="rounded-lg shadow-sm bg-white border border-zinc-200 block"
            />
          </div>
        )}

        {fileUrl && !isPdf && !isImage && (
          // Unknown format — just show raw text + a download link
          <div className="bg-zinc-900 rounded-lg p-3 max-h-full overflow-y-auto">
            <pre className="text-[11px] font-mono whitespace-pre-wrap text-zinc-100 leading-relaxed" data-testid="po-document-preview">
              {po.Raw_Text?.trim() || `(${po.Source_Filename} — preview not supported, use Open to download)`}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Progress panel during AI parsing ────────────────────────────────────────
// Gemini parsing typically takes 6-25s on PDFs/images. Showing a step-by-step
// progress UI is the difference between users thinking the app is frozen vs
// understanding work is in flight. Each step has a friendly label + a
// live-updating elapsed timer once the upload completes.
function ParseProgressPanel({ phase, elapsedMs, mode }) {
  const elapsedSec = Math.floor(elapsedMs / 1000);
  const steps = mode === "upload"
    ? [
        { key: "uploading", label: "Uploading the file" },
        { key: "parsing",   label: "Gemini is reading the document" },
        { key: "finalizing", label: "Saving the parsed PO" },
      ]
    : [
        { key: "parsing",   label: "Gemini is parsing the text" },
        { key: "finalizing", label: "Saving the parsed PO" },
      ];
  const currentIdx = Math.max(0, steps.findIndex((s) => s.key === phase));
  // Soft progress bar — sits between current step and next step, advancing
  // linearly with elapsed time so the user always sees motion. Caps at ~95%
  // until the real "done" status arrives.
  const phaseEstimateSec = phase === "parsing" ? 20 : 5;
  const phaseProgress = phase === "uploading"
    ? 25
    : phase === "parsing"
    ? Math.min(95, 30 + Math.round((elapsedSec / phaseEstimateSec) * 60))
    : phase === "finalizing" ? 95 : 5;

  return (
    <div className="border border-brand-200 bg-brand-50/40 rounded-xl p-4 space-y-3" data-testid="po-parse-progress">
      <div className="flex items-center gap-3">
        <div className="relative w-9 h-9 flex items-center justify-center flex-shrink-0">
          <svg className="absolute inset-0 animate-spin text-brand-600" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.18" strokeWidth="3" />
            <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          </svg>
          <Sparkles className="w-4 h-4 text-brand-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-zinc-900">{steps[currentIdx]?.label || "Working…"}</div>
          <div className="text-xs text-zinc-600">
            {phase === "parsing"
              ? `Usually takes 6–25 seconds. Elapsed: ${elapsedSec}s.`
              : phase === "uploading"
              ? "Sending the file to the server…"
              : "Almost done…"}
          </div>
        </div>
        <div className="text-xs font-mono text-brand-700 font-bold tabular-nums">{phaseProgress}%</div>
      </div>

      {/* Determinate-ish progress bar */}
      <div className="h-1.5 w-full rounded-full bg-zinc-200 overflow-hidden">
        <div
          className="h-full bg-brand-600 transition-[width] duration-700 ease-out"
          style={{ width: `${phaseProgress}%` }}
          data-testid="po-parse-progress-bar"
        />
      </div>

      {/* Step list */}
      <ol className="space-y-1.5">
        {steps.map((s, i) => {
          const done = i < currentIdx;
          const active = i === currentIdx;
          return (
            <li key={s.key} className="flex items-center gap-2 text-xs">
              <span className={[
                "w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0",
                done ? "bg-brand-600 text-white" :
                active ? "bg-white ring-2 ring-brand-500 text-brand-700" :
                "bg-zinc-200 text-zinc-500",
              ].join(" ")}>
                {done ? "✓" : i + 1}
              </span>
              <span className={done ? "text-zinc-500 line-through" : active ? "text-zinc-900 font-medium" : "text-zinc-400"}>
                {s.label}
              </span>
            </li>
          );
        })}
      </ol>
      <p className="text-[10px] text-zinc-500">Don&apos;t close this window — the new PO will pop into the inbox as soon as Gemini finishes.</p>
    </div>
  );
}

// ─── Parse modal ─────────────────────────────────────────────────────────────
function ParseModal({ onClose, onParsed }) {
  const [mode, setMode] = useState("paste"); // "paste" | "upload"
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  // 'idle' | 'uploading' | 'parsing' | 'finalizing'
  const [phase, setPhase] = useState("idle");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [err, setErr] = useState("");

  const parse = async () => {
    setParsing(true); setErr(""); setElapsedMs(0);
    try {
      if (mode === "paste") {
        setPhase("parsing");
        const { data } = await api.post("/purchase-orders/parse", { Raw_Text: text });
        onParsed(data);
      } else {
        setPhase("uploading");
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/purchase-orders/parse-file", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        // The endpoint returns immediately with Status='parsing' and Gemini
        // runs in the background. Poll the PO until it finishes (or fails).
        if (data?.Status === "parsing") {
          setPhase("parsing");
          const finalDoc = await pollPoUntilParsed(data.id, { onTick: setElapsedMs });
          setPhase("finalizing");
          if (finalDoc?.Status === "parse_error") {
            setErr(finalDoc.Parse_Error || "Parser failed.");
            onParsed(finalDoc); // still surface the row so user can retry / delete
          } else {
            onParsed(finalDoc);
          }
        } else {
          onParsed(data);
        }
      }
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setParsing(false); setPhase("idle"); }
  };

  const ready = mode === "paste" ? text.trim().length > 0 : Boolean(file);

  return (
    <Modal
      title="Parse PO with Gemini"
      icon={<Wand2 className="w-5 h-5 text-brand-600" />}
      onClose={onClose}
      size="2xl"
    >
      <div className="px-6 py-5 space-y-4">
        {/* Mode tabs */}
        <div className="flex gap-1 p-1 bg-zinc-100 rounded-lg" data-testid="po-parse-mode-tabs">
          <button
            data-testid="po-parse-mode-paste"
            onClick={() => setMode("paste")}
            className={[
              "flex-1 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors",
              mode === "paste" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-600 hover:text-zinc-900",
            ].join(" ")}
          >📋 Paste text</button>
          <button
            data-testid="po-parse-mode-upload"
            onClick={() => setMode("upload")}
            className={[
              "flex-1 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors",
              mode === "upload" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-600 hover:text-zinc-900",
            ].join(" ")}
          >📎 Upload file</button>
        </div>

        {mode === "paste" ? (
          <>
            <p className="text-sm text-zinc-600">Paste the PO email body (or PDF text). Gemini will extract customer, lines, quantities and delivery info.</p>
            <textarea
              data-testid="po-parse-textarea"
              rows={12}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={"From: ...\nSubject: PO #..."}
              className="es-input h-auto py-3 font-mono text-sm"
              disabled={parsing}
            />
          </>
        ) : (
          <UploadDropzone file={file} setFile={setFile} disabled={parsing} />
        )}

        {parsing && <ParseProgressPanel phase={phase} elapsedMs={elapsedMs} mode={mode} />}

        {err && <div className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-lg">{err}</div>}
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="es-btn-secondary" disabled={parsing}>Cancel</button>
          <button data-testid="po-parse-submit" disabled={parsing || !ready} onClick={parse} className="es-btn-primary">
            <Sparkles className="w-4 h-4" /> {parsing ? "Parsing with Gemini…" : (mode === "upload" ? "Upload & Parse" : "Parse")}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function UploadDropzone({ file, setFile, disabled }) {
  const accept = ".pdf,.png,.jpg,.jpeg,.webp,.docx,.txt,application/pdf,image/*";
  const onSelect = (f) => {
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) { alert("File too large (max 20 MB)."); return; }
    setFile(f);
  };
  return (
    <div>
      <p className="text-sm text-zinc-600 mb-3">
        Upload a PO as <strong>PDF, JPG, PNG, DOCX or TXT</strong> (max 20 MB). Gemini reads the file directly — no email server needed.
      </p>
      <label
        className={`block border-2 border-dashed rounded-xl p-8 text-center transition-colors ${disabled ? "border-zinc-200 bg-zinc-50/50 cursor-not-allowed opacity-60" : "border-zinc-300 hover:border-brand-400 cursor-pointer"}`}
        onDragOver={(e) => { if (disabled) return; e.preventDefault(); e.currentTarget.classList.add("border-brand-500", "bg-brand-50"); }}
        onDragLeave={(e) => { e.currentTarget.classList.remove("border-brand-500", "bg-brand-50"); }}
        onDrop={(e) => { if (disabled) return; e.preventDefault(); e.currentTarget.classList.remove("border-brand-500", "bg-brand-50"); onSelect(e.dataTransfer.files?.[0]); }}
      >
        <input
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => onSelect(e.target.files?.[0])}
          disabled={disabled}
          data-testid="po-parse-file-input"
        />
        {file ? (
          <div>
            <div className="font-semibold text-zinc-900">{file.name}</div>
            <div className="text-xs text-zinc-500 mt-1">{(file.size / 1024).toFixed(1)} KB · {file.type || "unknown"}</div>
            <div className="text-xs text-brand-700 mt-3 underline">Click to choose a different file</div>
          </div>
        ) : (
          <div>
            <div className="text-zinc-700 font-medium">Drop a PO file here, or click to browse</div>
            <div className="text-xs text-zinc-500 mt-2">PDF, JPG, PNG, DOCX, TXT — up to 20 MB</div>
          </div>
        )}
      </label>
    </div>
  );
}

// ─── Edit details modal (right-pane Edit Details button) ─────────────────────
function EditDetailsModal({ po, companies, onClose, onSaved }) {
  const [companyId, setCompanyId] = useState(po.Company_ID || "");
  const [orderRef, setOrderRef] = useState(po.Order_Reference || po.Parsed_Data?.po_reference || "");
  const [deliveryAddr, setDeliveryAddr] = useState(po.Parsed_Data?.delivery_address || "");
  const [comments, setComments] = useState(po.Comments || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const { data } = await api.patch(`/purchase-orders/${po.id}`, {
        Company_ID: companyId || null,
        Order_Reference: orderRef,
        Delivery_Address: deliveryAddr,
        Comments: comments,
      });
      onSaved(data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal
      title="Edit Details"
      icon={<Pencil className="w-4 h-4 text-brand-600" />}
      onClose={onClose}
      size="md"
      footer={
        <>
          <button onClick={onClose} className="es-btn-secondary">Cancel</button>
          <button data-testid="po-edit-save" disabled={saving} onClick={save} className="es-btn-primary">
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="px-6 py-5 space-y-3">
        <div>
          <label className="es-label">Resolved Company</label>
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="es-select w-full">
            <option value="">— none —</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.Company_Name}</option>)}
          </select>
        </div>
        <div>
          <label className="es-label">Order Reference</label>
          <input data-testid="po-edit-order-ref" value={orderRef} onChange={(e) => setOrderRef(e.target.value)} className="es-input" />
        </div>
        <div>
          <label className="es-label">Delivery Address</label>
          <input value={deliveryAddr} onChange={(e) => setDeliveryAddr(e.target.value)} className="es-input" />
        </div>
        <div>
          <label className="es-label">Comments / Instructions</label>
          <textarea rows={3} value={comments} onChange={(e) => setComments(e.target.value)} className="es-input h-auto py-2 resize-none" />
        </div>
        {err && <div className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-lg">{err}</div>}
      </div>
    </Modal>
  );
}
